<template>
  <div class="game-admin-page">
    <div class="game-admin-card">
      <div class="page-header">
        <div>
          <div class="page-title">游戏管理</div>
          <div class="page-subtitle">doro璇养成屋 · 实时状态、玩法配置、反馈审核</div>
        </div>
        <el-button type="primary" icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
      </div>

      <el-row :gutter="16" class="metric-row">
        <el-col :span="4" v-for="item in metrics" :key="item.key">
          <div class="metric-card">
            <div class="metric-value">{{ dashboard[item.key] || 0 }}</div>
            <div class="metric-label">{{ item.label }}</div>
          </div>
        </el-col>
      </el-row>

      <el-tabs v-model="activeTab">
        <el-tab-pane label="实时监控" name="overview">
          <el-alert
            :title="marketTitle"
            type="info"
            :closable="false"
            show-icon
            class="admin-alert"
          />
          <el-table :data="market" border height="560" style="width: 100%; margin-top: 14px">
            <el-table-column prop="stock_code" label="代码" width="90"></el-table-column>
            <el-table-column prop="name" label="公司" min-width="140"></el-table-column>
            <el-table-column prop="category" label="分类" width="120"></el-table-column>
            <el-table-column prop="price" label="当前价" width="110"></el-table-column>
            <el-table-column label="涨跌" width="110">
              <template slot-scope="scope">
                <span :class="Number(scope.row.change_rate) >= 0 ? 'up' : 'down'">{{ formatRate(scope.row.change_rate) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="volatility" label="波动" width="100"></el-table-column>
            <el-table-column label="增长计划" width="130">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.growth_plan" type="success" size="mini">增长中 {{ formatPlanRemain(scope.row.growth_plan) }}</el-tag>
                <span v-else class="muted">-</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="210">
              <template slot-scope="scope">
                <el-button type="text" @click="openMarket(scope.row)">编辑</el-button>
                <el-button type="text" @click="openKline(scope.row)">K线</el-button>
                <el-button type="text" @click="openGrowthPlan(scope.row)">增长</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="玩法配置" name="config">
          <el-form :model="configForm" label-width="180px" class="config-form">
            <el-form-item label="游戏开放">
              <el-switch v-model="configForm.game_enabled" active-text="开放" inactive-text="维护"></el-switch>
              <el-input v-model="configForm.game_maintenance_message" placeholder="维护提示" style="width: 320px; margin-left: 12px;"></el-input>
            </el-form-item>
            <el-form-item label="股市开市时间">
              <el-time-picker v-model="configForm.market_open_start" value-format="HH:mm" format="HH:mm" placeholder="开市"></el-time-picker>
              <span class="split">至</span>
              <el-time-picker v-model="configForm.market_open_end" value-format="HH:mm" format="HH:mm" placeholder="休市"></el-time-picker>
            </el-form-item>
            <el-form-item label="开市星期">
              <el-checkbox-group v-model="configForm.market_open_weekdays">
                <el-checkbox :label="1">周一</el-checkbox>
                <el-checkbox :label="2">周二</el-checkbox>
                <el-checkbox :label="3">周三</el-checkbox>
                <el-checkbox :label="4">周四</el-checkbox>
                <el-checkbox :label="5">周五</el-checkbox>
                <el-checkbox :label="6">周六</el-checkbox>
                <el-checkbox :label="7">周日</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <el-form-item label="股价刷新间隔">
              <el-input-number v-model="configForm.market_update_interval_seconds" :min="60" :max="3600" :step="60"></el-input-number>
              <span class="unit">秒</span>
            </el-form-item>
            <el-form-item label="股市波动倍率">
              <el-input-number v-model="configForm.market_volatility_multiplier" :min="0.2" :max="2.5" :step="0.1"></el-input-number>
            </el-form-item>
            <el-form-item label="休市探险加成">
              <el-input-number v-model="configForm.adventure_bonus_when_market_closed" :min="0" :max="0.6" :step="0.05"></el-input-number>
            </el-form-item>
            <el-form-item label="行动冷却">
              <el-input-number v-model="configForm.work_cooldown_seconds" :min="300" :max="86400" :step="300"></el-input-number>
              <span class="unit">打工秒</span>
              <el-input-number v-model="configForm.train_cooldown_seconds" :min="300" :max="86400" :step="300"></el-input-number>
              <span class="unit">训练秒</span>
              <el-input-number v-model="configForm.adventure_cooldown_seconds" :min="300" :max="86400" :step="300"></el-input-number>
              <span class="unit">探险秒</span>
            </el-form-item>
            <el-form-item label="经济参数">
              <el-input-number v-model="configForm.checkin_reward" :min="20" :max="500"></el-input-number>
              <span class="unit">签到</span>
              <el-input-number v-model="configForm.house_price_base" :min="500" :max="10000" :step="100"></el-input-number>
              <span class="unit">公寓基础价</span>
              <el-input-number v-model="configForm.market_trade_fee" :min="0" :max="0.05" :step="0.001"></el-input-number>
              <span class="unit">手续费</span>
            </el-form-item>
            <el-form-item label="审核奖励">
              <el-input-number v-model="configForm.feedback_reward_approved" :min="0" :max="500"></el-input-number>
              <span class="unit">星币</span>
            </el-form-item>
            <el-form-item label="进行时长">
              <el-input-number v-model="configForm.work_duration_seconds" :min="300" :max="1800" :step="60"></el-input-number>
              <span class="unit">打工秒</span>
              <el-input-number v-model="configForm.train_duration_seconds" :min="300" :max="1800" :step="60"></el-input-number>
              <span class="unit">训练秒</span>
              <el-input-number v-model="configForm.adventure_duration_seconds" :min="300" :max="1800" :step="60"></el-input-number>
              <span class="unit">探险秒</span>
            </el-form-item>
            <el-form-item label="医院与复活">
              <el-input-number v-model="configForm.hospital_rest_seconds" :min="300" :max="21600" :step="300"></el-input-number>
              <span class="unit">医院秒</span>
              <el-input-number v-model="configForm.revive_coin_cost" :min="100" :max="99999" :step="100"></el-input-number>
              <span class="unit">星币复活</span>
            </el-form-item>
            <el-form-item label="掉落与夜间奖励">
              <el-input-number v-model="configForm.item_drop_multiplier" :min="0" :max="5" :step="0.1"></el-input-number>
              <span class="unit">掉落倍率</span>
              <el-input-number v-model="configForm.nightly_reward_hour" :min="0" :max="23"></el-input-number>
              <span class="unit">发放小时</span>
            </el-form-item>
            <el-form-item label="随机事件">
              <el-input-number v-model="configForm.random_event_rate" :min="0" :max="1" :step="0.01"></el-input-number>
              <span class="unit">触发概率</span>
              <el-input-number v-model="configForm.random_buff_seconds" :min="60" :max="86400" :step="300"></el-input-number>
              <span class="unit">Buff 秒</span>
            </el-form-item>
            <el-form-item label="每日世界事件">
              <el-switch v-model="configForm.world_event_enabled" active-text="启用"></el-switch>
              <span class="unit">时间</span>
              <el-time-picker v-model="configForm.world_event_time" value-format="HH:mm" format="HH:mm" placeholder="触发时间"></el-time-picker>
              <el-button type="warning" icon="el-icon-magic-stick" @click="triggerWorldEvent">手动触发</el-button>
            </el-form-item>
            <el-form-item label="世界事件参数">
              <el-input-number v-model="configForm.world_event_temp_hours" :min="1" :max="168" :step="1"></el-input-number>
              <span class="unit">临时地点小时</span>
              <el-input-number v-model="configForm.world_event_shop_hours" :min="1" :max="168" :step="1"></el-input-number>
              <span class="unit">商店小时</span>
              <el-input-number v-model="configForm.world_event_shop_limit" :min="1" :max="20" :step="1"></el-input-number>
              <span class="unit">每人限购</span>
              <el-input-number v-model="configForm.world_event_shop_stock" :min="1" :max="999" :step="1"></el-input-number>
              <span class="unit">库存</span>
            </el-form-item>
            <el-form-item label="体力恢复间隔">
              <el-input-number v-model="configForm.stamina_recovery_high_interval_seconds" :min="30" :max="3600" :step="30"></el-input-number>
              <span class="unit">饥渴 &gt;80 秒/点</span>
              <el-input-number v-model="configForm.stamina_recovery_mid_interval_seconds" :min="30" :max="3600" :step="30"></el-input-number>
              <span class="unit">饥渴 60-80 秒/点</span>
            </el-form-item>
            <el-form-item label="玩家市场">
              <el-input-number v-model="configForm.trade_market_fee_rate" :min="0" :max="0.3" :step="0.005"></el-input-number>
              <span class="unit">手续费</span>
              <el-input-number v-model="configForm.trade_market_min_price_rate" :min="0.1" :max="1" :step="0.05"></el-input-number>
              <span class="unit">最低倍率</span>
              <el-input-number v-model="configForm.trade_market_max_price_rate" :min="1" :max="10" :step="0.1"></el-input-number>
              <span class="unit">最高倍率</span>
            </el-form-item>
            <el-form-item label="世界Boss刷新">
              <el-input-number v-model="configForm.world_boss_refresh_hour" :min="0" :max="23"></el-input-number>
              <span class="unit">刷新小时</span>
              <el-input-number v-model="configForm.world_boss_refresh_interval_days" :min="1" :max="30"></el-input-number>
              <span class="unit">间隔天</span>
            </el-form-item>
            <el-form-item label="Boss最后一击">
              <el-input-number v-model="configForm.boss_last_hit_coin_bonus" :min="0" :max="999999" :step="100"></el-input-number>
              <span class="unit">星币</span>
              <el-input-number v-model="configForm.boss_last_hit_drop_multiplier" :min="0" :max="20" :step="0.1"></el-input-number>
              <span class="unit">掉落倍率</span>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-check" @click="saveConfig">保存配置</el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="扩展玩法" name="expansion">
          <div class="toolbar">
            <div>
              <el-button type="primary" icon="el-icon-refresh" @click="loadExpansionAdmin">刷新扩展玩法</el-button>
              <el-button type="success" icon="el-icon-check" @click="saveExpansionConfig">保存扩展配置</el-button>
            </div>
            <div>
              <el-button type="warning" @click="runExpansionAction('draw_lottery')">立即开奖</el-button>
              <el-button type="warning" @click="runExpansionAction('open_black_market', { minutes: expansionActionForm.black_market_minutes })">开启黑市</el-button>
              <el-button @click="runExpansionAction('refresh_black_market')">刷新黑市</el-button>
              <el-button type="danger" @click="runExpansionAction('reset_story_dungeon_daily')">刷新探索次数</el-button>
            </div>
          </div>

          <el-row :gutter="14" class="expansion-metrics">
            <el-col :span="4" v-for="item in expansionMetricCards" :key="item.key">
              <div class="expansion-metric">
                <div class="expansion-metric-value">{{ item.value }}</div>
                <div class="expansion-metric-label">{{ item.label }}</div>
              </div>
            </el-col>
          </el-row>

          <el-card shadow="never" class="expansion-card">
            <div slot="header" class="card-header">
              <span>玩法参数</span>
              <span class="muted">彩票、黑市、声望、抽卡、探索</span>
            </div>
            <el-form :model="expansionConfigForm" label-width="150px" class="expansion-config-form">
              <el-form-item v-for="field in expansionAdmin.configs || []" :key="field.key" :label="field.label">
                <el-input-number v-model="expansionConfigForm[field.key]" :min="0" :max="999999" :step="configStep(field.key)"></el-input-number>
                <span class="unit">{{ field.unit }}</span>
                <span class="muted">{{ field.remark }}</span>
              </el-form-item>
            </el-form>
          </el-card>

          <el-row :gutter="16">
            <el-col :span="12">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header">
                  <span>手动运营</span>
                  <span class="muted">补发称号、声望、时间轴事件</span>
                </div>
                <el-form :model="expansionActionForm" label-width="100px">
                  <el-form-item label="玩家 UID">
                    <el-input-number v-model="expansionActionForm.uid" :min="1"></el-input-number>
                  </el-form-item>
                  <el-form-item label="发放声望">
                    <el-input-number v-model="expansionActionForm.reputation" :min="1" :max="999999" :step="10"></el-input-number>
                    <el-button type="primary" @click="grantExpansionReputation">发放</el-button>
                  </el-form-item>
                  <el-form-item label="发放称号">
                    <el-select v-model="expansionActionForm.title_key" filterable placeholder="选择称号" style="width: 260px">
                      <el-option v-for="title in expansionAdmin.titles || []" :key="title.key" :label="title.name" :value="title.key"></el-option>
                    </el-select>
                    <el-button type="primary" @click="grantExpansionTitle">发放</el-button>
                  </el-form-item>
                  <el-form-item label="黑市分钟">
                    <el-input-number v-model="expansionActionForm.black_market_minutes" :min="10" :max="240" :step="5"></el-input-number>
                  </el-form-item>
                  <el-form-item label="时间轴标题">
                    <el-input v-model="expansionActionForm.timeline_title" maxlength="60" placeholder="例如：天选姬阵营补给线改组"></el-input>
                  </el-form-item>
                  <el-form-item label="时间轴内容">
                    <el-input type="textarea" v-model="expansionActionForm.timeline_summary" :rows="3" maxlength="300" placeholder="记录会展示给玩家的世界观大事件"></el-input>
                  </el-form-item>
                  <el-form-item>
                    <el-button type="success" @click="addTimelineEvent">添加时间轴事件</el-button>
                  </el-form-item>
                </el-form>
              </el-card>
            </el-col>

            <el-col :span="12">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header">
                  <span>今日状态</span>
                  <span class="muted">彩票、黑市、抽卡限定</span>
                </div>
                <div class="status-line">彩票：{{ ((expansionAdmin.lottery || {}).draw || {}).draw_number || "未开奖" }}，购票 {{ (expansionAdmin.lottery || {}).ticket_total || 0 }} 注</div>
                <div class="status-line">黑市：{{ ((expansionAdmin.black_market || {}).is_open) ? "开启中" : "未开启" }}，商品 {{ (((expansionAdmin.black_market || {}).items) || []).length }} 件</div>
                <div class="status-line">抽卡限定：{{ ((expansionAdmin.gacha || {}).limited_item || {}).name || "暂无" }}，今日 {{ (expansionAdmin.gacha || {}).draw_total || 0 }} 抽</div>
                <el-table :data="((expansionAdmin.black_market || {}).items) || []" border height="250" style="width: 100%; margin-top: 12px">
                  <el-table-column prop="name" label="黑市商品" min-width="160"></el-table-column>
                  <el-table-column prop="rarity_label" label="品级" width="90"></el-table-column>
                  <el-table-column prop="black_market_price" label="价格" width="90"></el-table-column>
                  <el-table-column prop="black_market_stock" label="库存" width="80"></el-table-column>
                </el-table>
              </el-card>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="14">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header">
                  <span>任务配置</span>
                  <span class="muted">日常、周常、成就任务只读总览</span>
                </div>
                <el-table :data="expansionAdmin.tasks || []" border height="360" style="width: 100%">
                  <el-table-column prop="title" label="任务" min-width="150"></el-table-column>
                  <el-table-column label="类型" width="90">
                    <template slot-scope="scope">{{ taskTypeLabel(scope.row.type) }}</template>
                  </el-table-column>
                  <el-table-column prop="desc" label="描述" min-width="220" show-overflow-tooltip></el-table-column>
                  <el-table-column prop="min_level" label="等级" width="70"></el-table-column>
                  <el-table-column prop="target" label="目标" width="80"></el-table-column>
                  <el-table-column label="奖励" min-width="180">
                    <template slot-scope="scope">{{ rewardText(scope.row.reward) }}</template>
                  </el-table-column>
                </el-table>
              </el-card>
            </el-col>

            <el-col :span="10">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header">
                  <span>声望排行</span>
                  <span class="muted">声望影响掉落和商店折扣</span>
                </div>
                <el-table :data="expansionAdmin.reputation_leaderboard || []" border height="360" style="width: 100%">
                  <el-table-column prop="rank" label="#" width="55"></el-table-column>
                  <el-table-column prop="nickname" label="玩家" min-width="130"></el-table-column>
                  <el-table-column prop="title" label="称号" min-width="110"></el-table-column>
                  <el-table-column prop="reputation" label="声望" width="90"></el-table-column>
                </el-table>
              </el-card>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="8">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header"><span>阵营</span></div>
                <div v-for="faction in expansionAdmin.factions || []" :key="faction.key" class="info-row">
                  <div class="info-title">{{ faction.name }}</div>
                  <div class="muted">{{ faction.leader }} · {{ (faction.stock_codes || []).join(" / ") }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header"><span>职业与技能</span></div>
                <div v-for="career in expansionAdmin.careers || []" :key="career.key" class="info-row">
                  <div class="info-title">{{ career.name }} <span class="muted">Lv.{{ career.min_level }}</span></div>
                  <el-tag v-for="skill in career.skills || []" :key="skill.key" size="mini" class="skill-tag">{{ skill.name }}</el-tag>
                </div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card shadow="never" class="expansion-card">
                <div slot="header" class="card-header"><span>称号与探索</span></div>
                <div class="status-line">称号 {{ (expansionAdmin.titles || []).length }} 个，探索模板 {{ (expansionAdmin.dungeon_templates || []).length }} 个</div>
                <div v-for="title in (expansionAdmin.titles || []).slice(0, 8)" :key="title.key" class="info-row compact">
                  <span class="info-title">{{ title.name }}</span>
                  <span class="muted">{{ title.description }}</span>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <el-card shadow="never" class="expansion-card">
            <div slot="header" class="card-header">
              <span>世界观时间轴</span>
              <span class="muted">最新大事件</span>
            </div>
            <el-table :data="expansionAdmin.timeline || []" border height="320" style="width: 100%">
              <el-table-column prop="title" label="事件" min-width="180"></el-table-column>
              <el-table-column prop="event_type" label="类型" width="120"></el-table-column>
              <el-table-column prop="summary" label="内容" min-width="360" show-overflow-tooltip></el-table-column>
              <el-table-column label="时间" width="160">
                <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
              </el-table-column>
            </el-table>
          </el-card>
        </el-tab-pane>

        <el-tab-pane label="道具装备" name="items">
          <div class="toolbar">
            <div>
              <el-button type="primary" icon="el-icon-plus" @click="openItem()">新增道具</el-button>
              <el-button icon="el-icon-refresh" @click="loadItems">刷新</el-button>
            </div>
          </div>
          <el-table :data="items" border height="540" style="width: 100%">
            <el-table-column prop="item_key" label="Key" width="150"></el-table-column>
            <el-table-column prop="name" label="名称" min-width="150"></el-table-column>
            <el-table-column prop="item_type" label="类型" width="90"></el-table-column>
            <el-table-column prop="rarity_label" label="品级" width="100">
              <template slot-scope="scope"><span :style="{ color: scope.row.rarity_color, fontWeight: 700 }">{{ scope.row.rarity_label }}</span></template>
            </el-table-column>
            <el-table-column prop="slot" label="槽位" width="90"></el-table-column>
            <el-table-column prop="price" label="价格" width="90"></el-table-column>
            <el-table-column prop="drop_rate" label="掉率" width="90"></el-table-column>
            <el-table-column prop="shop_enabled" label="商店" width="80"></el-table-column>
            <el-table-column prop="description" label="描述" min-width="220" show-overflow-tooltip></el-table-column>
            <el-table-column fixed="right" label="操作" width="90">
              <template slot-scope="scope"><el-button type="text" @click="openItem(scope.row)">编辑</el-button></template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="副本/Boss" name="battle">
          <div class="toolbar">
            <div>
              <el-button type="primary" icon="el-icon-plus" @click="openLocation()">新增地点</el-button>
              <el-button icon="el-icon-refresh" @click="loadLocations">刷新地点</el-button>
            </div>
            <div>
              <el-button type="warning" icon="el-icon-s-flag" @click="spawnBoss">刷新世界Boss</el-button>
              <el-button type="danger" icon="el-icon-close" :disabled="!canKillBoss" @click="killBoss">击杀并结算</el-button>
            </div>
          </div>
          <el-alert
            :title="bossTitle"
            type="warning"
            :closable="false"
            show-icon
            class="admin-alert"
          />
          <el-table :data="locations" border height="320" style="width: 100%; margin-bottom: 16px">
            <el-table-column prop="location_key" label="Key" width="150"></el-table-column>
            <el-table-column prop="location_type" label="类型" width="100"></el-table-column>
            <el-table-column prop="name" label="名称" min-width="150"></el-table-column>
            <el-table-column prop="scene_key" label="场景" width="100"></el-table-column>
            <el-table-column prop="min_level" label="等级" width="80"></el-table-column>
            <el-table-column prop="duration_seconds" label="时长" width="90"></el-table-column>
            <el-table-column prop="enemy_hp" label="敌HP" width="90"></el-table-column>
            <el-table-column prop="enemy_attack" label="攻击" width="80"></el-table-column>
            <el-table-column prop="drop_multiplier" label="掉落倍率" width="100"></el-table-column>
            <el-table-column fixed="right" label="操作" width="90">
              <template slot-scope="scope"><el-button type="text" @click="openLocation(scope.row)">编辑</el-button></template>
            </el-table-column>
          </el-table>
          <div class="toolbar">
            <el-button type="primary" icon="el-icon-plus" @click="openBoss()">新增Boss模板</el-button>
            <el-button icon="el-icon-refresh" @click="loadBoss">刷新Boss</el-button>
          </div>
          <el-table :data="bossTemplates" border height="220" style="width: 100%">
            <el-table-column prop="boss_key" label="Key" width="150"></el-table-column>
            <el-table-column prop="name" label="名称" min-width="180"></el-table-column>
            <el-table-column prop="level" label="等级" width="80"></el-table-column>
            <el-table-column prop="max_hp" label="HP" width="110"></el-table-column>
            <el-table-column prop="attack" label="攻击" width="90"></el-table-column>
            <el-table-column prop="reward_coins" label="奖池" width="100"></el-table-column>
            <el-table-column prop="drop_multiplier" label="掉落倍率" width="100"></el-table-column>
            <el-table-column fixed="right" label="操作" width="90">
              <template slot-scope="scope"><el-button type="text" @click="openBoss(scope.row)">编辑</el-button></template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="发币奖励" name="grant">
          <el-form :model="grantForm" label-width="120px" class="config-form">
            <el-form-item label="玩家 UID">
              <el-input-number v-model="grantForm.uid" :min="1" :step="1"></el-input-number>
            </el-form-item>
            <el-form-item label="星币数量">
              <el-input-number v-model="grantForm.amount" :min="-999999" :max="999999" :step="100"></el-input-number>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-coin" @click="grantCoins">发放/扣除星币</el-button>
              <el-button type="success" icon="el-icon-present" @click="triggerNightly(false)">尝试发放今晚奖励</el-button>
              <el-button type="warning" icon="el-icon-refresh" @click="triggerNightly(true)">强制重发今日奖励</el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="交易市场" name="trade">
          <div class="toolbar">
            <div>
              <el-select v-model="tradeStatus" clearable placeholder="全部状态" style="width: 160px" @change="loadTradeListings">
                <el-option label="上架中" value="active"></el-option>
                <el-option label="已成交" value="sold"></el-option>
                <el-option label="已取消" value="canceled"></el-option>
              </el-select>
              <el-button icon="el-icon-refresh" @click="loadTradeListings">刷新</el-button>
            </div>
          </div>
          <el-table :data="tradeListings" border height="540" style="width: 100%">
            <el-table-column prop="id" label="ID" width="70"></el-table-column>
            <el-table-column label="物品" min-width="180">
              <template slot-scope="scope">
                <span :style="{ color: scope.row.item && scope.row.item.rarity_color, fontWeight: 700 }">
                  {{ scope.row.item && scope.row.item.name }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="quantity" label="数量" width="80"></el-table-column>
            <el-table-column prop="unit_price" label="单价" width="90"></el-table-column>
            <el-table-column prop="guide_price" label="指导价" width="90"></el-table-column>
            <el-table-column prop="total_price" label="总价" width="90"></el-table-column>
            <el-table-column prop="fee_amount" label="手续费" width="90"></el-table-column>
            <el-table-column prop="seller_name" label="卖家" min-width="140"></el-table-column>
            <el-table-column prop="buyer_name" label="买家" min-width="140"></el-table-column>
            <el-table-column prop="status" label="状态" width="90"></el-table-column>
            <el-table-column label="上架时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.listed_at) }}</template>
            </el-table-column>
            <el-table-column label="成交时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.sold_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="玩家" name="players">
          <div class="toolbar">
            <el-input v-model="keyword" placeholder="搜索昵称、B站UID、宠物名" clearable prefix-icon="el-icon-search" style="width: 280px" @keyup.enter.native="loadPlayers" />
            <el-button type="primary" icon="el-icon-search" @click="loadPlayers">搜索</el-button>
          </div>
          <el-table :data="players" border height="520" style="width: 100%">
            <el-table-column prop="uid" label="UID" width="80"></el-table-column>
            <el-table-column prop="nickname" label="玩家" min-width="150"></el-table-column>
            <el-table-column prop="pet_name" label="宠物" width="110"></el-table-column>
            <el-table-column prop="level" label="等级" width="80"></el-table-column>
            <el-table-column prop="power" label="战力" width="90"></el-table-column>
            <el-table-column prop="coins" label="星币" width="100"></el-table-column>
            <el-table-column prop="wealth" label="资产" width="110"></el-table-column>
            <el-table-column prop="house_level" label="公寓" width="80"></el-table-column>
            <el-table-column label="状态" min-width="170">
              <template slot-scope="scope">
                饥饿 {{ scope.row.hunger }} / 口渴 {{ scope.row.thirst }} / 心情 {{ scope.row.mood }}
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="反馈审核" name="feedback">
          <el-table :data="feedbackList" border height="540" style="width: 100%">
            <el-table-column prop="id" label="ID" width="70"></el-table-column>
            <el-table-column prop="nickname" label="玩家" width="150"></el-table-column>
            <el-table-column prop="feedback_type" label="类型" width="90"></el-table-column>
            <el-table-column prop="content" label="内容" min-width="300" show-overflow-tooltip></el-table-column>
            <el-table-column prop="status" label="状态" width="100"></el-table-column>
            <el-table-column label="时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column fixed="right" label="操作" width="110">
              <template slot-scope="scope">
                <el-button type="text" @click="openFeedback(scope.row)">处理</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="操作日志" name="logs">
          <el-table :data="logs" border height="540" style="width: 100%">
            <el-table-column prop="id" label="ID" width="70"></el-table-column>
            <el-table-column prop="action" label="动作" width="130"></el-table-column>
            <el-table-column prop="nickname" label="玩家" width="150"></el-table-column>
            <el-table-column label="详情" min-width="360">
              <template slot-scope="scope"><pre class="log-json">{{ prettyJson(scope.row.detail_json) }}</pre></template>
            </el-table-column>
            <el-table-column label="时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog title="编辑股票" :visible.sync="marketVisible" width="520px">
      <el-form :model="marketForm" label-width="100px">
        <el-form-item label="代码"><el-input v-model="marketForm.stock_code"></el-input></el-form-item>
        <el-form-item label="名称"><el-input v-model="marketForm.name"></el-input></el-form-item>
        <el-form-item label="分类"><el-input v-model="marketForm.category"></el-input></el-form-item>
        <el-form-item label="当前价"><el-input-number v-model="marketForm.price" :min="1" :max="9999"></el-input-number></el-form-item>
        <el-form-item label="波动"><el-input-number v-model="marketForm.volatility" :min="0.001" :max="0.3" :step="0.005"></el-input-number></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="marketForm.sort" :min="0"></el-input-number></el-form-item>
        <el-form-item label="启用"><el-switch v-model="marketForm.status" :active-value="1" :inactive-value="0"></el-switch></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="marketVisible = false">取消</el-button>
        <el-button type="primary" @click="saveMarket">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="klineTitle" :visible.sync="klineVisible" width="960px" @opened="renderKlineChart" @closed="destroyKlineChart">
      <div class="kline-head">
        <div>
          <span class="kline-price">当前价 {{ klineStock.price || "--" }}</span>
          <span :class="Number(klineStock.change_rate) >= 0 ? 'up' : 'down'">{{ formatRate(klineStock.change_rate) }}</span>
        </div>
        <el-button size="mini" icon="el-icon-refresh" :loading="klineLoading" @click="loadKline(klineStock)">刷新</el-button>
      </div>
      <div v-loading="klineLoading" class="kline-chart-wrap">
        <div ref="klineChart" class="kline-chart"></div>
      </div>
    </el-dialog>

    <el-dialog title="设置增长计划" :visible.sync="growthVisible" width="520px">
      <el-form :model="growthForm" label-width="120px">
        <el-form-item label="股票">
          <div>{{ growthStock.name || "--" }} <span class="muted">{{ growthStock.stock_code || "" }}</span></div>
        </el-form-item>
        <el-form-item label="持续小时">
          <el-input-number v-model="growthForm.hours" :min="1" :max="720" :step="1"></el-input-number>
        </el-form-item>
        <el-form-item label="增长上限">
          <el-input-number v-model="growthForm.max_growth_percent" :min="1" :max="100" :step="1"></el-input-number>
          <span class="unit">%</span>
        </el-form-item>
        <el-form-item label="波动比例">
          <el-input-number v-model="growthForm.volatility_percent" :min="0" :max="50" :step="0.5"></el-input-number>
          <span class="unit">%</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="growthVisible = false">取消</el-button>
        <el-button type="primary" :loading="growthLoading" @click="submitGrowthPlan">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="处理反馈" :visible.sync="feedbackVisible" width="560px">
      <el-form :model="feedbackForm" label-width="100px">
        <el-form-item label="内容"><div>{{ feedbackForm.content }}</div></el-form-item>
        <el-form-item label="状态">
          <el-select v-model="feedbackForm.status" style="width: 100%">
            <el-option label="待处理" value="pending"></el-option>
            <el-option label="处理中" value="processing"></el-option>
            <el-option label="已通过并奖励" value="approved"></el-option>
            <el-option label="已驳回" value="rejected"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="奖励星币"><el-input-number v-model="feedbackForm.reward_coins" :min="0" :max="500"></el-input-number></el-form-item>
        <el-form-item label="备注"><el-input type="textarea" v-model="feedbackForm.admin_note" :rows="4"></el-input></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="feedbackVisible = false">取消</el-button>
        <el-button type="primary" @click="saveFeedback">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="道具/装备配置" :visible.sync="itemVisible" width="720px">
      <el-form :model="itemForm" label-width="110px">
        <el-form-item label="Key"><el-input v-model="itemForm.item_key" placeholder="如 revive_ticket"></el-input></el-form-item>
        <el-form-item label="名称"><el-input v-model="itemForm.name"></el-input></el-form-item>
        <el-form-item label="类型">
          <el-select v-model="itemForm.item_type" style="width: 100%">
            <el-option label="使用类" value="use"></el-option>
            <el-option label="装备类" value="equipment"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="品级">
          <el-select v-model="itemForm.rarity" style="width: 100%">
            <el-option label="普通" value="white"></el-option>
            <el-option label="优秀" value="green"></el-option>
            <el-option label="稀有" value="blue"></el-option>
            <el-option label="史诗" value="purple"></el-option>
            <el-option label="传说" value="gold"></el-option>
            <el-option label="红色至宝" value="red"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="装备槽位"><el-input v-model="itemForm.slot" placeholder="weapon/armor/boots/accessory"></el-input></el-form-item>
        <el-form-item label="商店价格"><el-input-number v-model="itemForm.price" :min="0" :max="999999"></el-input-number></el-form-item>
        <el-form-item label="商店上架"><el-switch v-model="itemForm.shop_enabled" :active-value="1" :inactive-value="0"></el-switch></el-form-item>
        <el-form-item label="掉落概率"><el-input-number v-model="itemForm.drop_rate" :min="0" :max="1" :step="0.001"></el-input-number></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="itemForm.sort" :min="0"></el-input-number></el-form-item>
        <el-form-item label="启用"><el-switch v-model="itemForm.status" :active-value="1" :inactive-value="0"></el-switch></el-form-item>
        <el-form-item label="描述"><el-input type="textarea" v-model="itemForm.description" :rows="2"></el-input></el-form-item>
        <el-form-item label="效果 JSON">
          <el-input type="textarea" v-model="itemForm.effect_json_text" :rows="6" placeholder='如 {"heal":35} 或 {"stats":{"strength":3}}'></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="itemVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="探险/副本地点" :visible.sync="locationVisible" width="720px">
      <el-form :model="locationForm" label-width="110px">
        <el-form-item label="Key"><el-input v-model="locationForm.location_key"></el-input></el-form-item>
        <el-form-item label="类型">
          <el-select v-model="locationForm.location_type" style="width: 100%">
            <el-option label="探险" value="adventure"></el-option>
            <el-option label="副本" value="dungeon"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="名称"><el-input v-model="locationForm.name"></el-input></el-form-item>
        <el-form-item label="场景"><el-input v-model="locationForm.scene_key" placeholder="forest/dungeon/boss/market"></el-input></el-form-item>
        <el-form-item label="等级/时长">
          <el-input-number v-model="locationForm.min_level" :min="1"></el-input-number>
          <span class="unit">等级</span>
          <el-input-number v-model="locationForm.duration_seconds" :min="300" :max="3600" :step="60"></el-input-number>
          <span class="unit">秒</span>
        </el-form-item>
        <el-form-item label="基础奖励">
          <el-input-number v-model="locationForm.base_coins" :min="0" :max="99999"></el-input-number>
          <span class="unit">星币</span>
          <el-input-number v-model="locationForm.base_exp" :min="0" :max="9999"></el-input-number>
          <span class="unit">经验</span>
        </el-form-item>
        <el-form-item label="敌人属性">
          <el-input-number v-model="locationForm.enemy_hp" :min="0" :max="99999"></el-input-number>
          <span class="unit">HP</span>
          <el-input-number v-model="locationForm.enemy_attack" :min="0" :max="9999"></el-input-number>
          <span class="unit">攻击</span>
        </el-form-item>
        <el-form-item label="掉落倍率"><el-input-number v-model="locationForm.drop_multiplier" :min="0" :max="10" :step="0.1"></el-input-number></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="locationForm.sort" :min="0"></el-input-number></el-form-item>
        <el-form-item label="启用"><el-switch v-model="locationForm.status" :active-value="1" :inactive-value="0"></el-switch></el-form-item>
        <el-form-item label="描述"><el-input type="textarea" v-model="locationForm.description" :rows="3"></el-input></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="locationVisible = false">取消</el-button>
        <el-button type="primary" @click="saveLocation">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="世界 Boss 模板" :visible.sync="bossVisible" width="680px">
      <el-form :model="bossForm" label-width="110px">
        <el-form-item label="Key"><el-input v-model="bossForm.boss_key"></el-input></el-form-item>
        <el-form-item label="名称"><el-input v-model="bossForm.name"></el-input></el-form-item>
        <el-form-item label="等级/HP">
          <el-input-number v-model="bossForm.level" :min="1"></el-input-number>
          <span class="unit">等级</span>
          <el-input-number v-model="bossForm.max_hp" :min="1000" :max="999999"></el-input-number>
          <span class="unit">HP</span>
        </el-form-item>
        <el-form-item label="攻击/奖池">
          <el-input-number v-model="bossForm.attack" :min="1" :max="9999"></el-input-number>
          <span class="unit">攻击</span>
          <el-input-number v-model="bossForm.reward_coins" :min="100" :max="999999"></el-input-number>
          <span class="unit">星币</span>
        </el-form-item>
        <el-form-item label="掉落倍率"><el-input-number v-model="bossForm.drop_multiplier" :min="0" :max="20" :step="0.1"></el-input-number></el-form-item>
        <el-form-item label="场景"><el-input v-model="bossForm.scene_key" placeholder="boss"></el-input></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="bossForm.sort" :min="0"></el-input-number></el-form-item>
        <el-form-item label="启用"><el-switch v-model="bossForm.status" :active-value="1" :inactive-value="0"></el-switch></el-form-item>
        <el-form-item label="描述"><el-input type="textarea" v-model="bossForm.description" :rows="3"></el-input></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="bossVisible = false">取消</el-button>
        <el-button type="primary" @click="saveBoss">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { post } from "../axios/request.js";
import { formatTime, isSuccess, prettyJson } from "./appAdminUtils.js";

export default {
  name: "gameManage",
  data() {
    return {
      activeTab: "overview",
      dashboard: {},
      market: [],
      players: [],
      feedbackList: [],
      logs: [],
      items: [],
      locations: [],
      bossTemplates: [],
      currentBoss: {},
      tradeListings: [],
      expansionAdmin: {},
      expansionConfigForm: {},
      tradeStatus: "",
      keyword: "",
      configForm: {},
      marketVisible: false,
      marketForm: {},
      klineVisible: false,
      klineLoading: false,
      klineStock: {},
      klineCandles: [],
      klineChart: null,
      growthVisible: false,
      growthLoading: false,
      growthStock: {},
      growthForm: {
        stock_code: "",
        hours: 24,
        max_growth_percent: 10,
        volatility_percent: 3,
      },
      feedbackVisible: false,
      feedbackForm: {},
      itemVisible: false,
      itemForm: {},
      locationVisible: false,
      locationForm: {},
      bossVisible: false,
      bossForm: {},
      grantForm: {
        uid: 1,
        amount: 100,
      },
      expansionActionForm: {
        uid: 1,
        reputation: 50,
        title_key: "",
        timeline_title: "",
        timeline_summary: "",
        black_market_minutes: 30,
      },
      metrics: [
        { key: "total_players", label: "玩家数" },
        { key: "online_players", label: "在线" },
        { key: "today_actions", label: "24h操作" },
        { key: "today_chats", label: "24h聊天" },
        { key: "pending_feedback", label: "待审核" },
        { key: "coin_total", label: "总星币" },
        { key: "active_trade_listings", label: "市场上架" },
        { key: "trade_sold_today", label: "24h成交" },
      ],
    };
  },
  computed: {
    marketTitle() {
      const status = this.dashboard.market_status || {};
      return `${status.hint || "股市状态未知"} · ${status.now_text || ""}`;
    },
    klineTitle() {
      const stock = this.klineStock || {};
      const code = stock.stock_code ? ` (${stock.stock_code})` : "";
      return `${stock.name || "公司"}${code} K线`;
    },
    bossTitle() {
      const boss = this.currentBoss || {};
      if (!boss.id) return "当前没有世界 Boss";
      return `${boss.name || "世界 Boss"} · HP ${boss.hp_remaining || 0}/${boss.max_hp || 0} · 全服伤害 ${boss.total_damage || 0}`;
    },
    canKillBoss() {
      const boss = this.currentBoss || {};
      return !!boss.id && boss.status === "active";
    },
    expansionMetricCards() {
      const overview = (this.expansionAdmin && this.expansionAdmin.overview) || {};
      return [
        { key: "task_count", label: "任务", value: overview.task_count || 0 },
        { key: "title_count", label: "称号", value: overview.title_count || 0 },
        { key: "faction_count", label: "阵营", value: overview.faction_count || 0 },
        { key: "career_count", label: "职业", value: overview.career_count || 0 },
        { key: "dungeon_template_count", label: "探索", value: overview.dungeon_template_count || 0 },
        { key: "timeline_count", label: "时间轴", value: overview.timeline_count || 0 },
      ];
    },
  },
  mounted() {
    this.refreshAll();
    window.addEventListener("resize", this.resizeKlineChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeKlineChart);
    this.destroyKlineChart();
  },
  methods: {
    formatTime,
    prettyJson,
    refreshAll() {
      this.loadDashboard();
      this.loadConfig();
      this.loadPlayers();
      this.loadFeedback();
      this.loadLogs();
      this.loadItems();
      this.loadLocations();
      this.loadBoss();
      this.loadTradeListings();
      this.loadExpansionAdmin();
    },
    loadDashboard() {
      post("/api/admin/game/dashboard", {}).then((res) => {
        if (isSuccess(res)) {
          this.dashboard = res.data.data || {};
          this.market = this.dashboard.market || [];
        }
      });
    },
    loadConfig() {
      post("/api/admin/game/config", {}).then((res) => {
        if (isSuccess(res)) {
          this.configForm = Object.assign({}, (res.data.data || {}).config || {});
        }
      });
    },
    saveConfig() {
      post("/api/admin/game/config", { config: this.configForm }).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.loadDashboard();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    loadPlayers() {
      post("/api/admin/game/players", { keyword: this.keyword }).then((res) => {
        if (isSuccess(res)) this.players = res.data.data || [];
      });
    },
    loadFeedback() {
      post("/api/admin/game/feedback", {}).then((res) => {
        if (isSuccess(res)) this.feedbackList = res.data.data || [];
      });
    },
    loadLogs() {
      post("/api/admin/game/logs", { page: 1, size: 80 }).then((res) => {
        if (isSuccess(res)) this.logs = res.data.data || [];
      });
    },
    loadItems() {
      post("/api/admin/game/items", {}).then((res) => {
        if (isSuccess(res)) this.items = res.data.data || [];
      });
    },
    loadLocations() {
      post("/api/admin/game/locations", {}).then((res) => {
        if (isSuccess(res)) this.locations = res.data.data || [];
      });
    },
    loadBoss() {
      post("/api/admin/game/boss", {}).then((res) => {
        if (isSuccess(res)) {
          const data = res.data.data || {};
          this.bossTemplates = data.templates || [];
          this.currentBoss = data.current || {};
        }
      });
    },
    loadTradeListings() {
      post("/api/admin/game/trade/listings", { status: this.tradeStatus }).then((res) => {
        if (isSuccess(res)) this.tradeListings = res.data.data || [];
      });
    },
    loadExpansionAdmin() {
      post("/api/admin/game/expansion", {}).then((res) => {
        if (isSuccess(res)) {
          const data = res.data.data || {};
          this.expansionAdmin = data;
          this.expansionConfigForm = Object.assign({}, data.config || {});
          if (!this.expansionActionForm.title_key && (data.titles || []).length) {
            this.expansionActionForm.title_key = data.titles[0].key;
          }
        }
      });
    },
    saveExpansionConfig() {
      post("/api/admin/game/expansion/config", { config: this.expansionConfigForm }).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          const data = res.data.data || {};
          this.expansionAdmin = data;
          this.expansionConfigForm = Object.assign({}, data.config || {});
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    runExpansionAction(action, payload) {
      post("/api/admin/game/expansion/action", { action, payload: payload || {} }).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "操作成功", message: res.data.msg, type: "success" });
          const data = res.data.data || {};
          this.expansionAdmin = data;
          this.expansionConfigForm = Object.assign({}, data.config || this.expansionConfigForm);
        } else {
          this.$message.error(res.data.msg || "操作失败");
        }
      });
    },
    grantExpansionTitle() {
      this.runExpansionAction("grant_title", {
        uid: this.expansionActionForm.uid,
        title_key: this.expansionActionForm.title_key,
      });
    },
    grantExpansionReputation() {
      this.runExpansionAction("grant_reputation", {
        uid: this.expansionActionForm.uid,
        amount: this.expansionActionForm.reputation,
      });
    },
    addTimelineEvent() {
      this.runExpansionAction("timeline_add", {
        title: this.expansionActionForm.timeline_title,
        summary: this.expansionActionForm.timeline_summary,
        uid: this.expansionActionForm.uid,
      });
    },
    openMarket(row) {
      this.marketForm = Object.assign({ status: 1, sort: 0 }, row);
      this.marketVisible = true;
    },
    openKline(row) {
      this.klineStock = Object.assign({}, row || {});
      this.klineCandles = [];
      this.klineVisible = true;
      this.loadKline(row);
    },
    openGrowthPlan(row) {
      const stock = Object.assign({}, row || {});
      const plan = stock.growth_plan || {};
      const planHours = Number(plan.remaining_seconds || 0) > 0 ? Math.ceil(Number(plan.remaining_seconds || 0) / 3600) : 24;
      const planGrowth = Number(plan.max_growth_rate);
      const planVolatility = Number(plan.volatility_rate);
      const stockVolatility = Number(stock.volatility);
      this.growthStock = stock;
      this.growthForm = {
        stock_code: stock.stock_code || "",
        hours: Math.max(planHours, 1),
        max_growth_percent: Number.isFinite(planGrowth) && planGrowth > 0 ? Number((planGrowth * 100).toFixed(2)) : 10,
        volatility_percent: Number.isFinite(planVolatility)
          ? Number((planVolatility * 100).toFixed(2))
          : Number.isFinite(stockVolatility)
          ? Number((stockVolatility * 100).toFixed(2))
          : 3,
      };
      this.growthVisible = true;
    },
    loadKline(row) {
      const stockCode = (row && row.stock_code) || this.klineStock.stock_code;
      if (!stockCode) return;
      this.klineLoading = true;
      post("/api/admin/game/market/kline", { stock_code: stockCode, limit: 120 })
        .then((res) => {
          if (isSuccess(res)) {
            const data = res.data.data || {};
            this.klineStock = Object.assign({}, this.klineStock, data.stock || {});
            this.klineCandles = data.candles || [];
            this.$nextTick(this.renderKlineChart);
          } else {
            this.$message.error(res.data.msg || "K线加载失败");
          }
        })
        .finally(() => {
          this.klineLoading = false;
        });
    },
    renderKlineChart() {
      if (!this.klineVisible || !this.$refs.klineChart) return;
      if (!this.klineChart) this.klineChart = echarts.init(this.$refs.klineChart);
      const candles = this.klineCandles || [];
      const labels = candles.map((item) => this.formatKlineTime(item.time || item.bucket_at));
      const values = candles.map((item) => [
        Number(item.open || item.open_price || 0),
        Number(item.close || item.close_price || 0),
        Number(item.low || item.low_price || 0),
        Number(item.high || item.high_price || 0),
      ]);
      const volumes = candles.map((item, index) => [index, Number(item.volume || 0), values[index] && values[index][1] >= values[index][0] ? 1 : -1]);
      this.klineChart.setOption({
        animation: false,
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "cross" },
          formatter: (params) => {
            const candle = (params || []).find((item) => item.seriesType === "candlestick");
            const volume = (params || []).find((item) => item.seriesName === "成交量");
            if (!candle) return "";
            const value = candle.data || [];
            return [
              candle.axisValue,
              `开盘：${value[0]}`,
              `收盘：${value[1]}`,
              `最低：${value[2]}`,
              `最高：${value[3]}`,
              `成交量：${volume && volume.data ? volume.data[1] : 0}`,
            ].join("<br/>");
          },
        },
        grid: [
          { left: 58, right: 24, top: 24, height: 300 },
          { left: 58, right: 24, top: 350, height: 86 },
        ],
        xAxis: [
          { type: "category", data: labels, boundaryGap: true, axisLine: { lineStyle: { color: "#9aa4b2" } }, axisLabel: { color: "#6b7280" } },
          { type: "category", data: labels, gridIndex: 1, boundaryGap: true, axisLabel: { show: false }, axisTick: { show: false } },
        ],
        yAxis: [
          { scale: true, axisLine: { lineStyle: { color: "#9aa4b2" } }, splitLine: { lineStyle: { color: "#edf2f7" } }, axisLabel: { color: "#6b7280" } },
          { scale: true, gridIndex: 1, splitNumber: 2, axisLabel: { color: "#6b7280" }, splitLine: { show: false } },
        ],
        dataZoom: [
          { type: "inside", xAxisIndex: [0, 1], start: candles.length > 50 ? 45 : 0, end: 100 },
          { show: true, xAxisIndex: [0, 1], type: "slider", bottom: 8, height: 22, start: candles.length > 50 ? 45 : 0, end: 100 },
        ],
        series: [
          {
            name: "K线",
            type: "candlestick",
            data: values,
            itemStyle: {
              color: "#e53e3e",
              color0: "#16a34a",
              borderColor: "#e53e3e",
              borderColor0: "#16a34a",
            },
          },
          {
            name: "成交量",
            type: "bar",
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: volumes,
            itemStyle: {
              color: (params) => (params.data && params.data[2] >= 0 ? "#fca5a5" : "#86efac"),
            },
          },
        ],
      });
      this.resizeKlineChart();
    },
    resizeKlineChart() {
      if (this.klineChart) this.klineChart.resize();
    },
    destroyKlineChart() {
      if (this.klineChart) {
        this.klineChart.dispose();
        this.klineChart = null;
      }
    },
    formatKlineTime(value) {
      const time = Number(value || 0);
      if (!time) return "";
      const date = new Date(time * 1000);
      const pad = (num) => String(num).padStart(2, "0");
      return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    saveMarket() {
      post("/api/admin/game/market/save", this.marketForm).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.marketVisible = false;
          this.loadDashboard();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    submitGrowthPlan() {
      if (!this.growthForm.stock_code) return;
      this.growthLoading = true;
      post("/api/admin/game/market/growth-plan", this.growthForm)
        .then((res) => {
          if (isSuccess(res)) {
            const data = res.data.data || {};
            this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
            this.growthVisible = false;
            if (data.market) {
              this.market = data.market;
            } else {
              this.loadDashboard();
            }
          } else {
            this.$message.error(res.data.msg || "保存失败");
          }
        })
        .finally(() => {
          this.growthLoading = false;
        });
    },
    openFeedback(row) {
      this.feedbackForm = Object.assign({ reward_coins: this.configForm.feedback_reward_approved || 80 }, row);
      this.feedbackVisible = true;
    },
    saveFeedback() {
      post("/api/admin/game/updatefeedback", this.feedbackForm).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.feedbackVisible = false;
          this.loadFeedback();
          this.loadDashboard();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    openItem(row) {
      const base = {
        item_key: "",
        name: "",
        item_type: "use",
        rarity: "white",
        slot: "",
        price: 0,
        shop_enabled: 0,
        shop_stock: -1,
        drop_rate: 0,
        sort: 0,
        status: 1,
        description: "",
        effect_json_text: "{\n  \"heal\": 35\n}",
      };
      const item = Object.assign(base, row || {});
      item.effect_json_text = JSON.stringify((row && row.effect) || {}, null, 2);
      this.itemForm = item;
      this.itemVisible = true;
    },
    saveItem() {
      let effect = {};
      try {
        effect = JSON.parse(this.itemForm.effect_json_text || "{}");
      } catch (e) {
        this.$message.error("效果 JSON 格式不正确");
        return;
      }
      const payload = Object.assign({}, this.itemForm, { effect });
      post("/api/admin/game/item/save", payload).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.itemVisible = false;
          this.loadItems();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    openLocation(row) {
      this.locationForm = Object.assign(
        {
          location_key: "",
          location_type: "adventure",
          name: "",
          scene_key: "forest",
          min_level: 1,
          duration_seconds: 600,
          base_coins: 100,
          base_exp: 10,
          enemy_hp: 0,
          enemy_attack: 0,
          drop_multiplier: 1,
          sort: 0,
          status: 1,
          description: "",
        },
        row || {}
      );
      this.locationVisible = true;
    },
    saveLocation() {
      post("/api/admin/game/location/save", this.locationForm).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.locationVisible = false;
          this.loadLocations();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    openBoss(row) {
      this.bossForm = Object.assign(
        {
          boss_key: "",
          name: "",
          level: 1,
          max_hp: 10000,
          attack: 20,
          reward_coins: 3000,
          drop_multiplier: 3,
          scene_key: "boss",
          sort: 0,
          status: 1,
          description: "",
        },
        row || {}
      );
      this.bossVisible = true;
    },
    saveBoss() {
      post("/api/admin/game/boss/save", this.bossForm).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "保存成功", message: res.data.msg, type: "success" });
          this.bossVisible = false;
          this.loadBoss();
        } else {
          this.$message.error(res.data.msg || "保存失败");
        }
      });
    },
    spawnBoss() {
      post("/api/admin/game/boss/spawn", {}).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "Boss 已刷新", message: res.data.msg, type: "success" });
          this.loadBoss();
        } else {
          this.$message.error(res.data.msg || "刷新失败");
        }
      });
    },
    killBoss() {
      const boss = this.currentBoss || {};
      if (!boss.id) {
        this.$message.warning("当前没有可击杀的世界 Boss");
        return;
      }
      this.$confirm(`确认直接击杀 ${boss.name || "世界 Boss"}？将按当前伤害比例结算奖励，不发放最后一击奖励。`, "击杀世界 Boss", {
        confirmButtonText: "确认击杀",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          return post("/api/admin/game/boss/kill", { boss_id: boss.id });
        })
        .then((res) => {
          if (isSuccess(res)) {
            this.$notify({ title: "Boss 已击杀", message: res.data.msg, type: "success" });
            this.loadBoss();
            this.loadDashboard();
          } else {
            this.$message.error(res.data.msg || "击杀失败");
          }
        })
        .catch(() => {});
    },
    grantCoins() {
      post("/api/admin/game/grant-coins", this.grantForm).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "发放成功", message: res.data.msg, type: "success" });
          this.loadPlayers();
          this.loadDashboard();
        } else {
          this.$message.error(res.data.msg || "发放失败");
        }
      });
    },
    triggerNightly(force) {
      post("/api/admin/game/nightly-reward", { force }).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "夜间奖励", message: res.data.msg, type: "success" });
          this.loadDashboard();
        } else {
          this.$message.error(res.data.msg || "触发失败");
        }
      });
    },
    triggerWorldEvent() {
      post("/api/admin/game/world-event/trigger", {}).then((res) => {
        if (isSuccess(res)) {
          this.$notify({ title: "世界事件", message: res.data.msg, type: "success" });
          this.loadDashboard();
          this.loadItems();
          this.loadLocations();
        } else {
          this.$message.error(res.data.msg || "触发失败");
        }
      });
    },
    formatPlanRemain(plan) {
      const seconds = Number((plan && plan.remaining_seconds) || 0);
      if (seconds >= 3600) return `${Math.ceil(seconds / 3600)}小时`;
      if (seconds > 0) return `${Math.ceil(seconds / 60)}分钟`;
      return "即将结束";
    },
    formatRate(value) {
      const num = Number(value || 0);
      return `${num >= 0 ? "+" : ""}${(num * 100).toFixed(2)}%`;
    },
    configStep(key) {
      return /rate|bonus|discount/.test(key) ? 0.001 : 1;
    },
    taskTypeLabel(type) {
      return { daily: "日常", weekly: "周常", achievement: "成就" }[type] || type || "-";
    },
    rewardText(reward) {
      const value = reward || {};
      const parts = [];
      if (value.coins) parts.push(`${value.coins} 星币`);
      if (value.reputation) parts.push(`${value.reputation} 声望`);
      if (value.items && value.items.length) {
        parts.push(value.items.map((item) => `${item.item_key} x${item.quantity || 1}`).join("、"));
      }
      return parts.join("，") || "-";
    },
  },
};
</script>

<style scoped>
.game-admin-page {
  width: 100%;
  height: 100%;
  background: #f5f7fb;
  overflow: auto;
}
.game-admin-card {
  width: 98%;
  min-height: 95%;
  margin: 1.5% auto;
  padding: 22px;
  background: #fff;
  box-shadow: #e0e0e0 5px 5px 10px;
  box-sizing: border-box;
}
.page-header,
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.page-title {
  font-size: 22px;
  color: #1f2d3d;
  font-weight: 700;
}
.page-subtitle {
  color: #7f8fa4;
  margin-top: 6px;
}
.metric-row {
  margin-bottom: 12px;
}
.metric-card {
  height: 86px;
  margin-top: 10px;
  border-radius: 8px;
  padding: 16px;
  color: #fff;
  background: linear-gradient(135deg, #16a085, #38d9a9);
  box-sizing: border-box;
}
.metric-value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}
.metric-label {
  margin-top: 10px;
  font-size: 13px;
}
.admin-alert {
  margin-bottom: 8px;
}
.config-form {
  max-width: 1050px;
}
.split,
.unit {
  margin: 0 10px;
  color: #7f8fa4;
}
.up {
  color: #e53e3e;
  font-weight: 700;
}
.down {
  color: #16a34a;
  font-weight: 700;
}
.muted {
  color: #9aa4b2;
}
.kline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.kline-price {
  margin-right: 14px;
  color: #1f2d3d;
  font-weight: 700;
}
.kline-chart-wrap {
  height: 470px;
}
.kline-chart {
  width: 100%;
  height: 470px;
}
.log-json {
  max-height: 70px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  color: #4a5568;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}
.expansion-metrics {
  margin: 0 0 12px;
}
.expansion-metric {
  height: 76px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  box-sizing: border-box;
}
.expansion-metric-value {
  font-size: 22px;
  line-height: 1;
  font-weight: 800;
  color: #1f2937;
}
.expansion-metric-label {
  margin-top: 10px;
  color: #64748b;
  font-size: 13px;
}
.expansion-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
}
.expansion-config-form {
  max-height: 360px;
  overflow: auto;
}
.status-line {
  line-height: 28px;
  color: #334155;
}
.info-row {
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
}
.info-row:last-child {
  border-bottom: 0;
}
.info-row.compact {
  display: flex;
  gap: 8px;
  align-items: center;
}
.info-title {
  color: #1f2937;
  font-weight: 700;
}
.skill-tag {
  margin: 6px 6px 0 0;
}
</style>
